import React, { useMemo } from 'react';
import './OrderCardBubbles.css';

const BUBBLE_COUNT = 48;
const SIZE_MIN = 3;
const SIZE_MAX = 6;
const CARD_HEIGHT_PX = 450;

const RARITY_BUBBLE_COLORS = {
  common: {
    inner: 'rgba(255, 255, 255, 0.9)',
    mid: 'rgba(180, 180, 180, 0.7)',
    outer: 'rgba(128, 128, 128, 0.4)',
    shadow1: 'rgba(160, 160, 160, 0.7)',
    shadow2: 'rgba(128, 128, 128, 0.45)',
    shadow3: 'rgba(100, 100, 100, 0.3)',
    inset: 'rgba(220, 220, 220, 0.4)',
  },
  rare: {
    inner: 'rgba(220, 235, 255, 0.95)',
    mid: 'rgba(150, 190, 255, 0.7)',
    outer: 'rgba(65, 105, 225, 0.4)',
    shadow1: 'rgba(120, 160, 255, 0.85)',
    shadow2: 'rgba(65, 105, 225, 0.55)',
    shadow3: 'rgba(50, 80, 200, 0.35)',
    inset: 'rgba(240, 245, 255, 0.5)',
  },
  epic: {
    inner: 'rgba(245, 230, 255, 0.95)',
    mid: 'rgba(200, 160, 255, 0.7)',
    outer: 'rgba(147, 112, 219, 0.4)',
    shadow1: 'rgba(180, 140, 255, 0.85)',
    shadow2: 'rgba(147, 112, 219, 0.55)',
    shadow3: 'rgba(120, 90, 200, 0.35)',
    inset: 'rgba(255, 250, 255, 0.5)',
  },
  legendary: {
    inner: 'rgba(255, 245, 200, 0.95)',
    mid: 'rgba(255, 220, 120, 0.7)',
    outer: 'rgba(255, 180, 60, 0.4)',
    shadow1: 'rgba(255, 230, 150, 0.9)',
    shadow2: 'rgba(255, 200, 80, 0.6)',
    shadow3: 'rgba(255, 180, 50, 0.35)',
    inset: 'rgba(255, 250, 220, 0.5)',
  },
};

/**
 * Rarity-colored floating bubbles overlay for order cards (mulligan, opened rewards).
 * Renders on top of the card; use inside a position: relative container that wraps the order.
 */
export function OrderCardBubbles({ rarity = 'common', className = '' }) {
  const configs = useMemo(() => {
    return Array.from({ length: BUBBLE_COUNT }, () => {
      const r = Math.random();
      const H = CARD_HEIGHT_PX;
      let endYPx;
      if (r < 0.2) {
        endYPx = -(H * 0.07 + Math.random() * H * 0.18);
      } else if (r < 0.4) {
        endYPx = -(H * 0.25 + Math.random() * H * 0.25);
      } else if (r < 0.6) {
        endYPx = -(H * 0.5 + Math.random() * H * 0.25);
      } else {
        endYPx = -(H * 0.75 + Math.random() * H * 0.25);
      }
      return {
        left: 5 + Math.random() * 87,
        bottom: Math.random() * 3,
        size: Math.round(SIZE_MIN + Math.random() * (SIZE_MAX - SIZE_MIN)),
        delay: -(Math.random() * 3),
        duration: 0.4 + Math.random() * 0.85,
        endYPx,
      };
    });
  }, []);

  const r = (rarity || 'common').toString().toLowerCase();
  const colors = RARITY_BUBBLE_COLORS[r] || RARITY_BUBBLE_COLORS.common;

  return (
    <div
      className={`order-card-bubbles ${className}`.trim()}
      aria-hidden="true"
      style={{
        ['--bubble-inner']: colors.inner,
        ['--bubble-mid']: colors.mid,
        ['--bubble-outer']: colors.outer,
        ['--bubble-shadow1']: colors.shadow1,
        ['--bubble-shadow2']: colors.shadow2,
        ['--bubble-shadow3']: colors.shadow3,
        ['--bubble-inset']: colors.inset,
      }}
    >
      {configs.map((cfg, i) => (
        <span
          key={i}
          className="order-card-bubble"
          style={{
            left: `${cfg.left}%`,
            bottom: `${cfg.bottom}%`,
            width: cfg.size,
            height: cfg.size,
            animationDelay: `${cfg.delay}s`,
            animationDuration: `${cfg.duration}s`,
            ['--bubble-end-px']: `${cfg.endYPx}px`,
          }}
        />
      ))}
    </div>
  );
}

export default OrderCardBubbles;
